import { useState, useCallback, useEffect, useRef } from 'react'
import { useTaskContext } from './TaskContext'
import { throwIfProfileRegenerating } from '@/api/profileRegenError'
import { useProfileRegenGuard } from '@/components/ProfileRegenBlockProvider'

interface ApplicationTaskOptions {
  token: string
  onCompleted?: (results: any[], taskId: string, scanId: string) => void
  onError?: (error: Error, taskId: string | null, scanId: string) => void
}

export function useApplicationTask({
  token,
  onCompleted,
  onError,
}: ApplicationTaskOptions) {
  const {
    tasks,
    startTask,
    trackTaskProgress,
    findTaskByScanId,
    findActiveTaskByScanId,
    isTaskActive,
    refreshTasksState,
  } = useTaskContext()

  const guardRegen = useProfileRegenGuard()

  // State to track if component is mounted to handle navigation properly
  const isMountedRef = useRef(true)

  // state to track associations (scanId -> jobId)
  const [applicationJobs, setApplicationJobs] = useState<
    Record<string, string>
  >({})

  // state to track active subscriptions
  const [activeSubscriptions, setActiveSubscriptions] = useState<
    Record<string, { unsubscribe: () => void }>
  >({})

  useEffect(() => {
    // Force refresh when the component using this hook mounts
    refreshTasksState()

    // Set the mounted flag
    isMountedRef.current = true

    // Cleanup when unmounting
    return () => {
      // Unsubscribe from all active subscriptions first
      Object.values(activeSubscriptions).forEach((subscription) => {
        subscription.unsubscribe()
      })

      isMountedRef.current = false
    }
  }, [refreshTasksState, activeSubscriptions])

  // restore any existing jobs from TaskContext on mount
  useEffect(() => {
    const existingJobs: Record<string, string> = {}

    // find all application tasks with scan_id
    Object.values(tasks).forEach((task) => {
      if (
        task.type === 'application' &&
        task.scan_id &&
        task.status !== 'cleaned_up'
      ) {
        existingJobs[task.scan_id] = task.id
      }
    })

    // Only update if there are differences
    if (JSON.stringify(existingJobs) !== JSON.stringify(applicationJobs)) {
      setApplicationJobs(existingJobs)
    }
  }, [tasks, applicationJobs])

  // track task progress for jobs that need tracking
  useEffect(() => {
    Object.entries(applicationJobs).forEach(([, jobId]) => {
      // Get the current task state
      const task = tasks[jobId]

      // Only track if:
      // 1. We don't have an active subscription for this job
      // 2. The task exists
      // 3. We have a token
      // 4. The task is in a state that can be tracked
      if (
        !activeSubscriptions[jobId] &&
        task &&
        token &&
        ['connecting', 'starting', 'running'].includes(task.status)
      ) {
        // The trackTaskProgress now returns a subscription object with an unsubscribe method
        const subscription = trackTaskProgress(jobId, token)

        // Save the subscription
        setActiveSubscriptions(
          (prev) =>
            ({
              ...prev,
              [jobId]: subscription,
            }) as Record<string, { unsubscribe: () => void }>,
        )
      }
    })
  }, [applicationJobs, tasks, token, trackTaskProgress, activeSubscriptions])

  // monitor task status changes and clean up applicationJobs
  useEffect(() => {
    // Check if any tasks referenced in applicationJobs have been completed or errored
    Object.entries(applicationJobs).forEach(([scanId, jobId]) => {
      const task = tasks[jobId]

      // If the task is now cleaned_up, completed, error, or doesn't exist
      if (!task || ['cleaned_up', 'completed', 'error'].includes(task.status)) {
        // For completed or error tasks, we can process their results
        if (task) {
          if (task.status === 'completed' && isMountedRef.current) {
            // Query invalidation is handled directly in TaskContext when tasks complete

            // Still call onCompleted for any component-specific logic (like setGenerating(false))
            if (onCompleted) {
              onCompleted([], jobId, scanId)
            }
          } else if (
            task.status === 'error' &&
            onError &&
            isMountedRef.current
          ) {
            const errorObj =
              typeof task.error === 'string'
                ? new Error(task.error)
                : task.error ||
                  new Error('Failed to generate application. Please try again.')

            onError(errorObj, jobId, scanId)
          }
        }

        // Only for cleaned_up or non-existent tasks, remove from applicationJobs
        if (!task || task.status === 'cleaned_up') {
          setApplicationJobs((prev) => {
            const newJobs = { ...prev }
            delete newJobs[scanId]
            return newJobs
          })
        }

        // If we have an active subscription for this task, unsubscribe and remove it
        if (activeSubscriptions[jobId]) {
          activeSubscriptions[jobId].unsubscribe()

          setActiveSubscriptions((prev) => {
            const newSubscriptions = { ...prev }
            delete newSubscriptions[jobId]
            return newSubscriptions
          })
        }
      }
    })
  }, [tasks, applicationJobs, activeSubscriptions, onCompleted, onError])

  // start a new application generation
  const startApplicationGeneration = useCallback(
    async (scanId: string, needId?: number, orgUuid?: string) => {
      if (!scanId || !token) return null

      try {
        // check if there's an existing active task for this scan
        const existingTask = findActiveTaskByScanId(scanId, 'application')

        // if there's an existing task, return its ID
        if (existingTask) {
          return existingTask.id
        }

        // start the application generation - requires auth token
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/applications/${scanId}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          },
        )

        if (!response.ok) {
          await throwIfProfileRegenerating(response)
          if (response.status === 402) {
            // Payment required - extract the detailed error message
            const errorData = await response.json()
            const error = new Error(
              errorData.detail || 'Upgrade required to access this feature',
            )
            error.name = 'PaymentRequired'
            throw error
          }
          throw new Error(
            `Failed to start application generation: ${response.statusText}`,
          )
        }

        const data = await response.json()
        const jobId = data.job_id

        // register in global TaskContext with needId and orgUuid
        startTask(
          'application',
          jobId,
          needId ? String(needId) : undefined,
          orgUuid,
          scanId,
        )

        // store the job ID for this scan id locally - triggers useEffect that handles tracking
        setApplicationJobs((prev) => ({
          ...prev,
          [scanId]: jobId,
        }))

        return jobId
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error))
        // A profile-regen 409 opens the shared block dialog (updating / retry) —
        // don't also surface it as a generic application error.
        if (guardRegen(err)) return null
        onError?.(err, null, scanId)
        return null
      }
    },
    [token, findActiveTaskByScanId, startTask, onError, guardRegen],
  )

  // start a regeneration for an existing application
  // Registers the job in TaskContext keyed by scan_id, the same way as
  // initial generation, so MatchResultsListItem and ApplicationPage share state.
  const startApplicationRegeneration = useCallback(
    async (
      appId: number,
      scanId: string,
      needId?: number,
      orgUuid?: string,
    ) => {
      if (!appId || !scanId || !token) return null

      try {
        // if there's an existing active task for this scan, return its ID
        const existingTask = findActiveTaskByScanId(scanId, 'application')
        if (existingTask) return existingTask.id

        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/applications/regenerate/${appId}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          },
        )

        if (!response.ok) {
          await throwIfProfileRegenerating(response)
          if (response.status === 402) {
            const errorData = await response.json()
            const error = new Error(
              errorData.detail || 'Upgrade required to access this feature',
            )
            error.name = 'PaymentRequired'
            throw error
          }
          throw new Error(
            `Failed to start application regeneration: ${response.statusText}`,
          )
        }

        const data = await response.json()
        const jobId = data.job_id

        startTask(
          'application',
          jobId,
          needId ? String(needId) : undefined,
          orgUuid,
          scanId,
        )

        setApplicationJobs((prev) => ({
          ...prev,
          [scanId]: jobId,
        }))

        return jobId
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error))
        // A profile-regen 409 opens the shared block dialog (updating / retry) —
        // don't also surface it as a generic application error.
        if (guardRegen(err)) return null
        onError?.(err, null, scanId)
        return null
      }
    },
    [token, findActiveTaskByScanId, startTask, onError, guardRegen],
  )

  // Helper functions that use TaskContext
  const hasActiveApplicationGeneration = useCallback(
    (scanId: string) => {
      const task = findActiveTaskByScanId(scanId, 'application')
      return isTaskActive(task)
    },
    [findActiveTaskByScanId, isTaskActive],
  )

  const isLoadingForScanId = useCallback(
    (scanId: string) => {
      const task = findActiveTaskByScanId(scanId, 'application')
      return task?.status === 'connecting' || task?.status === 'starting'
    },
    [findActiveTaskByScanId],
  )

  const errorForScanId = useCallback(
    (scanId: string) => {
      // Look for both active and completed tasks
      const task = findTaskByScanId(scanId, 'application')
      return task?.error || null
    },
    [findTaskByScanId],
  )

  const progressForScanId = useCallback(
    (scanId: string) => {
      const task = findActiveTaskByScanId(scanId, 'application')
      return task?.progress || 0
    },
    [findActiveTaskByScanId],
  )

  const statusForScanId = useCallback(
    (scanId: string) => {
      const task = findActiveTaskByScanId(scanId, 'application')
      return task?.status || 'idle'
    },
    [findActiveTaskByScanId],
  )

  const isApplicationGenerationStaleForScan = useCallback(
    (scanId: string) => {
      const task = findActiveTaskByScanId(scanId, 'application')
      if (!task) return false

      // if status is already terminal, it's not stale
      if (['completed', 'error', 'cleaned_up'].includes(task.status)) {
        return false
      }

      // check for staleness based on startTime
      const startTime = new Date(task.startTime).getTime()
      const now = new Date().getTime()
      const runningTimeMinutes = (now - startTime) / (1000 * 60)

      return runningTimeMinutes > 15 // 15 minutes timeout
    },
    [findActiveTaskByScanId],
  )

  const resetApplicationGenerationForScanId = useCallback(
    (scanId: string) => {
      const jobId = applicationJobs[scanId]
      if (jobId) {
        // If we have an active subscription, unsubscribe
        if (activeSubscriptions[jobId]) {
          activeSubscriptions[jobId].unsubscribe()

          setActiveSubscriptions((prev) => {
            const newSubscriptions = { ...prev }
            delete newSubscriptions[jobId]
            return newSubscriptions
          })
        }

        // remove from local state
        setApplicationJobs((prev) => {
          const newJobs = { ...prev }
          delete newJobs[scanId]
          return newJobs
        })
      }
    },
    [applicationJobs, activeSubscriptions],
  )

  // get job ID for a scan ID
  const getJobIdForScanId = useCallback(
    (scanId: string) => {
      // first check local state
      const localJobId = applicationJobs[scanId]
      if (localJobId) return localJobId

      // then check TaskContext
      const task = findTaskByScanId(scanId, 'application')
      return task?.id || null
    },
    [applicationJobs, findTaskByScanId],
  )

  const refreshApplicationState = useCallback(() => {
    refreshTasksState()
  }, [refreshTasksState])

  return {
    startApplicationGeneration,
    startApplicationRegeneration,
    getJobIdForScanId,
    hasActiveApplicationGeneration,
    isLoadingForScanId,
    errorForScanId,
    progressForScanId,
    statusForScanId,
    isApplicationGenerationStaleForScan,
    resetApplicationGenerationForScanId,
    refreshApplicationState,
  }
}
